import { useEffect, useRef, useState } from 'react';
import { Divider, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme, useThemeUtils } from '@theme/useTheme';
import { SearchOutlined } from '@ant-design/icons';
import { SidebarLogo } from './SidebarLogo';
import { SidebarMenu } from './SidebarMenu';
import { SidebarFooterBar } from './SidebarFooterBar';
import { MenuSearch } from '@shared/components/Menu/MenuSearch';
import { useMenuKeyboard } from '@shared/hooks/useMenuKeyboard';
import type { AntdMenuItem } from '@shared/types/menu';
import './SidebarContent.css';

const SIDEBAR_SCROLL_DELAY_MS = 100;

interface SidebarContentProps {
  menuItems: AntdMenuItem[];
  selectedKeys: string[];
  openKeys: string[];
  onOpenChange: (keys: string[]) => void;
  onClick: (e: { key: string }) => void;
  collapsed: boolean;
  isMobile: boolean;
  isCollapsedDesktop: boolean;
  isShortSidebarHeight: boolean;
  onMenuNavigate?: () => void;
}

/**
 * Основной контент сайдбара (логотип + меню + настройки)
 */
export function SidebarContent({
  menuItems,
  selectedKeys,
  openKeys,
  onOpenChange,
  onClick,
  collapsed,
  isMobile,
  isCollapsedDesktop,
  onMenuNavigate,
}: SidebarContentProps) {
  const { t } = useTranslation(['translation', 'menu']);
  const { theme, isDark } = useTheme();
  const themeUtils = useThemeUtils();
  const { token } = theme;
  const [searchOpen, setSearchOpen] = useState(false);
  const menuContainerRef = useRef<HTMLDivElement>(null);
  const previousOpenKeysRef = useRef(new Set(openKeys));
  const pendingRootRevealRef = useRef<string | null>(null);
  const selectedKeysSignature = selectedKeys.join('\u0000');
  const openKeysSignature = openKeys.join('\u0000');
  const rootMenuKeysSignature = menuItems
    .flatMap((item) => (item && 'key' in item ? [String(item.key)] : []))
    .join('\u0000');

  useEffect(() => {
    if (isCollapsedDesktop || selectedKeysSignature.length === 0) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const selectedItem = menuContainerRef.current?.querySelector(
        '.ant-menu-item-selected',
      ) as HTMLElement | null;
      selectedItem?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, SIDEBAR_SCROLL_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isCollapsedDesktop, selectedKeysSignature]);

  useEffect(() => {
    const previousOpenKeys = previousOpenKeysRef.current;
    const currentOpenKeys = openKeysSignature.length > 0
      ? openKeysSignature.split('\u0000')
      : [];
    const rootMenuKeys = new Set(
      rootMenuKeysSignature.length > 0
        ? rootMenuKeysSignature.split('\u0000')
        : [],
    );
    previousOpenKeysRef.current = new Set(currentOpenKeys);

    if (isCollapsedDesktop) {
      pendingRootRevealRef.current = null;
      return;
    }

    const newlyOpenedKey = [...currentOpenKeys]
      .reverse()
      .find((key) => rootMenuKeys.has(key) && !previousOpenKeys.has(key));
    if (newlyOpenedKey) {
      pendingRootRevealRef.current = newlyOpenedKey;
    } else if (
      pendingRootRevealRef.current &&
      !currentOpenKeys.includes(pendingRootRevealRef.current)
    ) {
      pendingRootRevealRef.current = null;
    }

    const targetRootKey = pendingRootRevealRef.current;
    if (!targetRootKey) {
      return;
    }
    if (!rootMenuKeys.has(targetRootKey)) {
      pendingRootRevealRef.current = null;
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (pendingRootRevealRef.current !== targetRootKey) {
        return;
      }
      pendingRootRevealRef.current = null;

      const container = menuContainerRef.current;
      if (!container) {
        return;
      }

      const rootTitle = Array.from(
        container.querySelectorAll<HTMLElement>(
          '.ant-menu-submenu-title[data-menu-id][aria-expanded="true"]',
        ),
      ).find((title) => title.dataset.menuId?.endsWith(`-${targetRootKey}`));
      const rootItem = rootTitle?.closest<HTMLElement>('li.ant-menu-submenu');
      if (!rootItem) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const rootRect = rootItem.getBoundingClientRect();
      const isClipped = rootRect.top < containerRect.top || rootRect.bottom > containerRect.bottom;
      if (isClipped) {
        rootItem.scrollIntoView({ behavior: 'auto', block: 'nearest' });
      }
    }, SIDEBAR_SCROLL_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isCollapsedDesktop, openKeysSignature, rootMenuKeysSignature]);

  // Keyboard navigation
  useMenuKeyboard({
    items: menuItems,
    isEnabled: !isCollapsedDesktop,
    onSearchOpen: () => setSearchOpen(true),
  });

  const handleSearchSelect = (key: string) => {
    onClick({ key });
    setSearchOpen(false);
  };

  const primary = token.colorPrimary;
  const primaryStrong = token.colorPrimaryHover ?? token.colorPrimaryActive ?? primary;

  return (
    <div
      className="flex h-full flex-col sidebar-root"
      style={{
        // CSS-переменные для использования в стилях сайдбара (глобально проброшены, но дублируем локально)
        ['--color-primary' as string]: primary,
        ['--color-primary-strong' as string]: primaryStrong,
        ['--color-primary-shadow' as string]: themeUtils.alpha(primary, 0.3),
        ['--color-primary-hover' as string]: themeUtils.alpha(primary, 0.15),
        ['--color-primary-hover-bg' as string]: themeUtils.alpha(primary, 0.08),
        ['--color-primary-popup' as string]: themeUtils.alpha(primary, 0.12),
        ['--color-primary-popup-strong' as string]: themeUtils.alpha(primary, 0.2),
      }}
    >
      {/* Логотип */}
      <SidebarLogo
        collapsed={collapsed}
        isMobile={isMobile}
        onNavigate={onMenuNavigate}
      />

      {!isCollapsedDesktop && (
        <Divider
          style={{
            marginTop: 12,
            marginBottom: 8,
            marginLeft: token.paddingSM,
            marginRight: token.paddingSM,
            width: 'auto',
            minWidth: 0,
            borderColor: token.colorSplit,
          }}
        />
      )}

      {/* Поиск в меню (только когда не свернуто) */}
      {!isCollapsedDesktop && (
        <>
          {searchOpen ? (
            <MenuSearch
              items={menuItems}
              onItemSelect={handleSearchSelect}
              onClose={() => setSearchOpen(false)}
            />
          ) : (
            <div
              style={{
                padding: '8px 12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: `1px solid ${token.colorSplit}`,
              }}
            >
              <Tooltip
                title={t('menu:search_tooltip') || 'Поиск в меню (Ctrl+K)'}
                placement="right"
              >
                <div
                  className="menu-search-trigger"
                  onClick={() => setSearchOpen(true)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSearchOpen(true);
                    }
                  }}
                >
                  <SearchOutlined style={{ color: token.colorTextSecondary }} />
                  <span
                    style={{
                      fontSize: '12px',
                      color: token.colorTextTertiary,
                      marginLeft: '8px',
                    }}
                  >
                    {t('menu:search') || 'Поиск...'}
                  </span>
                </div>
              </Tooltip>
              <kbd
                style={{
                  fontSize: '10px',
                  padding: '2px 6px',
                  background: token.colorBgContainer,
                  border: `1px solid ${token.colorBorder}`,
                  borderRadius: '4px',
                  color: token.colorTextSecondary,
                }}
              >
                Ctrl+K
              </kbd>
            </div>
          )}
        </>
      )}

      {/* Меню */}
      {isCollapsedDesktop ? (
        // Своя область прокрутки: без неё длинная рейка вытесняет нижнюю строку за экран.
        <div className="flex-1 min-h-0" style={{ overflowY: 'auto' }}>
          <SidebarMenu
            menuItems={menuItems}
            selectedKeys={selectedKeys}
            openKeys={openKeys}
            onOpenChange={onOpenChange}
            onClick={onClick}
            inlineCollapsed={true}
            isDark={isDark}
          />
        </div>
      ) : (
        <>
          {!searchOpen && (
            <Divider
              style={{
                marginTop: 12,
                marginBottom: 8,
                marginLeft: token.paddingSM,
                marginRight: token.paddingSM,
                width: 'auto',
                minWidth: 0,
                borderColor: token.colorSplit,
              }}
            />
          )}
          <div
            className="flex-1 min-h-0 sidebar-menu-container"
            ref={menuContainerRef}
            style={{
              paddingLeft: token.paddingSM,
              paddingRight: token.paddingSM,
              paddingBottom: token.paddingMD,
              overflowY: 'auto',
            }}
          >
            <SidebarMenu
              menuItems={menuItems}
              selectedKeys={selectedKeys}
              openKeys={openKeys}
              onOpenChange={onOpenChange}
              onClick={onClick}
              inlineCollapsed={false}
              isDark={isDark}
            />
          </div>
        </>
      )}

      {/* Низ сайдбара: настройки меню и версия */}
      <SidebarFooterBar collapsed={isCollapsedDesktop} isMobile={isMobile} />
    </div>
  );
}
